import { interfaceError } from '../../../i18n/errorMessages';
import toast from '../../../i18n/toast';
import LocalizedMessage from '../../common/LocalizedMessage';
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { STATUS } from "../../common/Const";
import { hasErrors, hasNoErrors } from "../../common/Helpers";
import { history } from "../../../redux/store/configureStore";
import * as clientActions from "../../../redux/actions/clientActions";
import * as clientLedgerActions from "../../../redux/actions/clientLedgerActions";
import * as enrollmentActions from "../../../redux/actions/enrollmentActions";
import * as lessonRegisterActions from "../../../redux/actions/lessonRegisterActions";
import * as productActions from "../../../redux/actions/productActions";
import * as schoolClassActions from "../../../redux/actions/schoolClassActions";
import * as studentActions from "../../../redux/actions/studentActions";
import { buildSchoolYearRange } from "../../common/schoolYear";
import {
  CHARGING_STRATEGIES,
  ENROLLMENT_STATUSES,
  PARTICIPATION_STRATEGIES,
  PAYMENT_STRATEGIES,
  SCHEDULE_ITEM_SOURCE_TYPES,
} from "../../billingEnrollment/billingEnrollmentTypes";
import {
  buildPaymentScheduleItems,
  validateEnrollment,
} from "../../billingEnrollment/billingEnrollmentValidation";
import { buildManageRouteWithSchoolYear, getNormalizedSchoolYearStartYearFromSearch } from "../manageSchoolYearRouting";
import EnrollmentForm from "./EnrollmentForm";
import PackageStatusPanel from "./PackageStatusPanel";
import { useTranslation } from 'react-i18next';

const todayDate = () => new Date().toISOString().slice(0, 10);
const getProductById = (products, id) => products.find(product => product.id === id) || null;
const labelForPerson = item => `${item.firstName || ""} ${item.lastName || ""}`.trim();

function buildSchedulePreview(enrollment, product) {
  if (!product) return enrollment.paymentScheduleItems || [];
  if (product.paymentStrategy !== PAYMENT_STRATEGIES.INSTALLMENTS) return [];

  const dueDate = enrollment.startDate || todayDate();
  const previewItems = [];
  const defaultPrice = Number(product.defaultPrice || 0);

  if (defaultPrice > 0) {
    previewItems.push({
      dueDate,
      amount: defaultPrice,
      sourceType: product.paymentStrategy === "prepaid"
        ? SCHEDULE_ITEM_SOURCE_TYPES.PREPAID_PACKAGE
        : SCHEDULE_ITEM_SOURCE_TYPES.INSTALLMENT,
      description: product.paymentStrategy === "installments" ? "Rata 1" : "Oplata za produkt",
    });
  }

  return buildPaymentScheduleItems({
    enrollmentId: enrollment.id || "new-enrollment",
    items: previewItems,
  });
}

function shouldGenerateSchedule(enrollment) {
  return !(enrollment.scheduleManuallyEdited || (enrollment.paymentScheduleItems || []).length > 0);
}

function decorateEnrollment(enrollment, clients, products) {
  const product = getProductById(products, enrollment.productId) || enrollment.product || null;
  const client = clients.find(item => item.id === enrollment.clientId);

  return {
    ...enrollment,
    product: product || enrollment.product,
    productName: product ? product.name : enrollment.productName,
    clientDisplayName: client ? labelForPerson(client) : enrollment.clientDisplayName,
    paymentScheduleItems: shouldGenerateSchedule(enrollment)
      ? buildSchedulePreview(enrollment, product)
      : enrollment.paymentScheduleItems || [],
  };
}

const enrollmentRecordKey = enrollment => (enrollment && enrollment.id ? enrollment.id : "new");

function isBlankNewEnrollment(enrollment = {}) {
  return !enrollment.id &&
    !enrollment.clientId &&
    !enrollment.productId &&
    (enrollment.studentIds || []).length === 0 &&
    (enrollment.eligibleStudentIds || []).length === 0;
}

export function reconcileEnrollmentFromProps(currentEnrollment, initialEnrollment, clients, products) {
  const recordChanged = enrollmentRecordKey(currentEnrollment) !== enrollmentRecordKey(initialEnrollment);
  const sourceEnrollment = recordChanged || isBlankNewEnrollment(currentEnrollment)
    ? initialEnrollment
    : currentEnrollment;

  return decorateEnrollment(sourceEnrollment, clients, products);
}

export function buildEnrollmentSavePayload(enrollment) {
  const { product } = enrollment;
  const enrollmentForSave = { ...enrollment };
  delete enrollmentForSave.product;
  delete enrollmentForSave.scheduleManuallyEdited;
  const isPackageProduct = product && product.chargingStrategy === CHARGING_STRATEGIES.CONSUME_PACKAGE_UNITS;
  const usesPaymentSchedule = product && product.paymentStrategy === PAYMENT_STRATEGIES.INSTALLMENTS;
  const status = isPackageProduct && (!enrollmentForSave.status || enrollmentForSave.status === ENROLLMENT_STATUSES.DRAFT)
    ? "awaitingPayment"
    : enrollmentForSave.status || ENROLLMENT_STATUSES.DRAFT;
  const payload = {
    ...enrollmentForSave,
    status,
    termsSnapshot: product,
    paymentScheduleItems: usesPaymentSchedule ? enrollmentForSave.paymentScheduleItems || [] : [],
  };

  if (product && product.participationStrategy === PARTICIPATION_STRATEGIES.SHARED_STUDENTS) {
    return {
      ...payload,
      eligibleStudentIds: (enrollmentForSave.studentIds && enrollmentForSave.studentIds.length > 0)
        ? enrollmentForSave.studentIds
        : enrollmentForSave.eligibleStudentIds || [],
      studentIds: [],
    };
  }

  return {
    ...payload,
    eligibleStudentIds: [],
  };
}

function Enrollment({
  enrollment: initialEnrollment,
  enrollments,
  clientLedger,
  clients,
  lessonRegister,
  students,
  schoolClasses,
  products,
  loadClients,
  loadStudents,
  loadProducts,
  loadEnrollments,
  loadClientLedger,
  loadLessonRegisters,
  loadSchoolClasses,
  saveEnrollment,
  activatePackageEnrollment,
  extendPackageValidity,
}) {
  const { t: tr } = useTranslation();
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const [enrollment, setEnrollment] = useState(decorateEnrollment(initialEnrollment, clients, products));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(STATUS.IDLE);

  useEffect(() => {
    if (clients.length === 0) loadClients().catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    if (students.length === 0) loadStudents().catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    if (products.length === 0) loadProducts().catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    if (enrollments.length === 0) loadEnrollments().catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    if (schoolClasses.length === 0) {
      loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
        .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
    if (lessonRegister.length === 0) {
      loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
        .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    clients.length,
    enrollments.length,
    lessonRegister.length,
    loadClients,
    loadEnrollments,
    loadLessonRegisters,
    loadProducts,
    loadSchoolClasses,
    loadStudents,
    products.length,
    schoolClasses.length,
    students.length,
  ]);

  useEffect(() => {
    if (enrollment.clientId) {
      loadClientLedger(enrollment.clientId).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
  }, [enrollment.clientId, loadClientLedger]);

  useEffect(() => {
    setEnrollment(currentEnrollment => reconcileEnrollmentFromProps(currentEnrollment, initialEnrollment, clients, products));
  }, [clients, initialEnrollment, products]);

  const validate = (nextEnrollment = enrollment) => {
    const nextErrors = validateEnrollment(nextEnrollment);
    setErrors(nextErrors);
    return hasNoErrors(nextErrors);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const selectedValues = event.target.selectedOptions
      ? Array.from(event.target.selectedOptions).map(option => option.value)
      : [];
    const product = name === "productId" ? getProductById(products, value) : getProductById(products, enrollment.productId);
    let nextEnrollment = {
      ...enrollment,
      [name]: value,
    };

    if (name === "studentIds") {
      nextEnrollment = { ...nextEnrollment, studentIds: value ? [value] : [] };
    }

    if (name === "eligibleStudentIds") {
      nextEnrollment = {
        ...nextEnrollment,
        eligibleStudentIds: selectedValues,
        studentIds: [],
      };
    }

    if (name === "clientId") {
      nextEnrollment = {
        ...nextEnrollment,
        studentIds: [],
        eligibleStudentIds: [],
      };
    }

    if (name === "productId") {
      nextEnrollment = {
        ...nextEnrollment,
        productId: value,
        product,
        scheduleManuallyEdited: false,
        paymentScheduleItems: [],
      };
    }

    nextEnrollment = decorateEnrollment(nextEnrollment, clients, products);
    setEnrollment(nextEnrollment);
    validate(nextEnrollment);
  };

  const handleScheduleItemChange = (itemId, field, value) => {
    const nextEnrollment = {
      ...enrollment,
      scheduleManuallyEdited: true,
      paymentScheduleItems: (enrollment.paymentScheduleItems || []).map(item => (
        item.id === itemId ? { ...item, [field]: value } : item
      )),
    };
    setEnrollment(nextEnrollment);
    validate(nextEnrollment);
  };

  const handleAddScheduleItem = () => {
    const dueDate = enrollment.startDate || todayDate();
    const nextEnrollment = {
      ...enrollment,
      scheduleManuallyEdited: true,
      paymentScheduleItems: [
        ...(enrollment.paymentScheduleItems || []),
        {
          id: `${enrollment.id || "new-enrollment"}#manual#${Date.now()}`,
          dueDate,
          amount: 0,
          sourceType: SCHEDULE_ITEM_SOURCE_TYPES.INSTALLMENT,
          description: tr("manage:extraScheduledPayment_47"),
          status: "planned",
        },
      ],
    };
    setEnrollment(nextEnrollment);
    validate(nextEnrollment);
  };

  const handleRemoveScheduleItem = (itemId) => {
    const nextEnrollment = {
      ...enrollment,
      scheduleManuallyEdited: true,
      paymentScheduleItems: (enrollment.paymentScheduleItems || []).filter(item => item.id !== itemId),
    };
    setEnrollment(nextEnrollment);
    validate(nextEnrollment);
  };

  const handleCancel = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/enrollments", "", activeSchoolYearStartYear));
  };

  const buildSavePayload = () => {
    return buildEnrollmentSavePayload(enrollment);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const payload = buildSavePayload();

    if (!validate(payload)) {
      setStatus(STATUS.SUBMITTED);
      return;
    }

    saveEnrollment(payload)
      .then(() => {
        setStatus(STATUS.COMPLETED);
        handleCancel();
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handlePackageCommand = (action, command) => {
    setStatus(STATUS.SUBMITTING);
    return action(enrollment.id, command)
      .then((savedEnrollment) => {
        setEnrollment(decorateEnrollment(savedEnrollment, clients, products));
        setStatus(STATUS.COMPLETED);
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  return (
    <div className="mb-5">
      {hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}><LocalizedMessage isError value={errors[key]} /></div>)}
        </div>
      )}
      <EnrollmentForm
        clients={clients}
        students={students}
        products={products}
        enrollment={enrollment}
        errors={errors}
        onChange={handleChange}
        onScheduleItemChange={handleScheduleItemChange}
        onAddScheduleItem={handleAddScheduleItem}
        onRemoveScheduleItem={handleRemoveScheduleItem}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        status={status}
      />
      <PackageStatusPanel
        enrollment={enrollment}
        clientLedger={clientLedger}
        students={students}
        lessonRegister={lessonRegister}
        schoolClasses={schoolClasses}
        onActivate={command => handlePackageCommand(activatePackageEnrollment, command)}
        onExtendValidity={command => handlePackageCommand(extendPackageValidity, command)}
        disabled={status === STATUS.SUBMITTING}
      />
    </div>
  );
}

Enrollment.propTypes = {
  enrollment: PropTypes.object.isRequired,
  enrollments: PropTypes.array.isRequired,
  clientLedger: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  schoolClasses: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadProducts: PropTypes.func.isRequired,
  loadEnrollments: PropTypes.func.isRequired,
  loadClientLedger: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  saveEnrollment: PropTypes.func.isRequired,
  activatePackageEnrollment: PropTypes.func.isRequired,
  extendPackageValidity: PropTypes.func.isRequired,
};

const newEnrollment = {
  status: "draft",
  startDate: todayDate(),
  studentIds: [],
  paymentScheduleItems: [],
};
const getEnrollmentById = (enrollments, id) => enrollments.find(enrollment => enrollment.id === id) || newEnrollment;

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  const enrollment = id === "new" || state.enrollments.length === 0 ? newEnrollment : getEnrollmentById(state.enrollments, id);

  return {
    enrollment,
    enrollments: state.enrollments,
    clientLedger: state.clientLedger.filter(entry => !enrollment.clientId || entry.clientId === enrollment.clientId),
    clients: state.clients.filter(client => !client.deleted),
    lessonRegister: state.lessonRegister,
    students: state.students.filter(student => !student.deleted),
    schoolClasses: state.schoolClasses,
    products: state.products.filter(product => !product.deleted),
  };
};

const mapDispatchToProps = dispatch => ({
  loadClients: () => dispatch(clientActions.loadClients()),
  loadStudents: () => dispatch(studentActions.loadStudents()),
  loadProducts: () => dispatch(productActions.loadProducts()),
  loadEnrollments: () => dispatch(enrollmentActions.loadEnrollments()),
  loadClientLedger: clientId => dispatch(clientLedgerActions.loadClientLedger(clientId)),
  loadLessonRegisters: (fromDate, toDate) => dispatch(lessonRegisterActions.loadLessonRegisters(fromDate, toDate)),
  loadSchoolClasses: (fromDate, toDate) => dispatch(schoolClassActions.loadSchoolClasses(fromDate, toDate)),
  saveEnrollment: enrollment => dispatch(enrollmentActions.saveEnrollment(enrollment)),
  activatePackageEnrollment: (enrollmentId, command) => dispatch(enrollmentActions.activatePackageEnrollment(enrollmentId, command)),
  extendPackageValidity: (enrollmentId, command) => dispatch(enrollmentActions.extendPackageValidity(enrollmentId, command)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Enrollment);
